import { useState, useRef, useEffect } from "react";
import { Box, Typography, Slider } from "@mui/material";
import { useTranslation } from "react-i18next";

interface SliderCaptchaProps {
  onVerify: (verified: boolean) => void;
  onReset?: () => void;
}

/**
 * A simple slider-based CAPTCHA component
 * User must slide to the end to verify they are human
 */
function SliderCaptcha({ onVerify, onReset }: SliderCaptchaProps) {
  const { t } = useTranslation();
  const [sliderValue, setSliderValue] = useState(0);
  const [verified, setVerified] = useState(false);
  const [failed, setFailed] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    if (verified || failed) return;

    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setSliderValue(value);

    // Check if slider reached the end (≥95%)
    if (value >= 95) {
      setVerified(true);
      onVerify(true);
    }
  };

  const handleSliderChangeCommitted = (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;

    // If user released slider before reaching the end, mark as failed
    if (!verified && value < 95) {
      setFailed(true);

      // Reset after 1 second
      timeoutRef.current = setTimeout(() => {
        setSliderValue(0);
        setFailed(false);
        onReset?.();
      }, 1000);
    }
  };

  const getSliderColor = () => {
    if (verified) return "success";
    if (failed) return "error";
    return "primary";
  };

  const getStatusText = () => {
    if (verified) {
      return t("captcha.verified", { defaultValue: "✓ Verified" });
    }
    if (failed) {
      return t("captcha.failed", { defaultValue: "✗ Try again" });
    }
    return t("captcha.slideToVerify", { defaultValue: "Slide to verify you are human →" });
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        border: 1,
        borderRadius: 1,
        borderColor: verified ? "success.main" : failed ? "error.main" : "divider",
        bgcolor: verified ? "success.lighter" : failed ? "error.lighter" : "background.paper",
        transition: "all 0.3s ease",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          textAlign: "center",
          color: verified ? "success.main" : failed ? "error.main" : "text.secondary",
          fontWeight: verified || failed ? 600 : 400,
        }}
      >
        {getStatusText()}
      </Typography>

      <Slider
        value={sliderValue}
        onChange={handleSliderChange}
        onChangeCommitted={handleSliderChangeCommitted}
        disabled={verified}
        color={getSliderColor()}
        aria-label="Slide to verify"
        valueLabelDisplay="off"
        sx={{
          "& .MuiSlider-thumb": {
            width: 32,
            height: 32,
            transition: "all 0.3s ease",
            "&:hover, &.Mui-focusVisible": {
              boxShadow: verified ? "0 0 0 8px rgba(46, 125, 50, 0.16)" : "0 0 0 8px rgba(25, 118, 210, 0.16)",
            },
          },
          "& .MuiSlider-track": {
            height: 8,
          },
          "& .MuiSlider-rail": {
            height: 8,
            opacity: 0.5,
          },
        }}
      />
    </Box>
  );
}

export default SliderCaptcha;
